import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { SelectItem } from '@components/controlled-input-select/src/controlled-input-select.type';
import { createFieldsFromFormFields } from '@components/forms/utils/create-fields-from-form-fields';
import { filterByRole } from '@components/forms/utils/filter-by-role';
import { AddGuestBody, FormField } from '@components/modals/add-holder-modal/src/add-holder-modal.type';
import { ERROR_NO_CHANNELS_ASSIGNED } from '@constants/error-codes/global';
import { SORT_ASC } from '@constants/global';
import { STEP_INFO, STEP_SELECT_SCOPE, STEP_SELECT_TYPE, STEP_SUCCESS } from '@constants/process-steps';
import { IMPORT_CONTEXT } from '@constants/queryParams';
import { CompanyAuthContext } from '@contexts/company-auth-context/company-auth-context';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { ModalContext } from '@contexts/modal/modal-context';
import { BOOKING_FORM_FIELDS, TICKET_HOLDER_FORM_FIELDS } from '@domain/admission';
import { EventListItem } from '@domain/event';
import { Pass } from '@domain/passes';
import { useGetOfferAddon } from '@hooks/addons/use-get-offer-addons';
import { useGetCompanyChannels } from '@hooks/company/use-get-company-channels';
import { useGetEventList } from '@hooks/events/use-get-event-list';
import { useImportMultiInvite } from '@hooks/import/use-import-multi-invite';
import { useGetCompanyPasses } from '@hooks/passes/use-get-company-passes';
import { useAddGuest } from '@hooks/use-add-guest/use-add-guest';
import { useBulkProcessNavigation } from '@hooks/use-bulk-process-navigation';
import { useProcessForm } from '@hooks/use-process-form/use-process-form';
import { UseProcessFormReturn } from '@hooks/use-process-form/use-process-form.types';
import { IMPORT_TYPE_OFFLINE_SALE } from '@pages/events/utils/import-process-utils';
import { handleBulkProcessSubmission } from '@utils/bulk-process/bulk-process-utils';
import { formatCurrency } from '@utils/price-formatter/price-formatter';
import moment from 'moment';

import { OfflineSalesEntryGate } from './components/entry-gate';
import { OfflineSalesSteps } from './offline-sales-steps';
import {
  MULTIPLE_OFFLINE_SELL,
  OFFLINE_SALES_SCOPE_EVENT,
  OFFLINE_SALES_SCOPE_PASS,
  OfflineSalesForm,
  OfflineSalesScope,
  ProcessOfflineSalesStep,
  SINGLE_OFFLINE_SELL,
} from './process-offline-sales.type';

/*
 * ProcessOfflineSales Component
 *
 * This component manages the process of recording offline sales for an event, supporting both single and bulk (multi-sale) flows.
 *
 * Features:
 * - Allows selection of sale type (single or multiple), event, offer, and channel.
 * - Collects customer and sale details via a form.
 * - Supports bulk offline sales by parsing multi-line text input for multiple sales.
 * - Integrates with shared bulk process utilities for parsing, submission, and navigation.
 * - Displays success and summary modals upon completion.
 *
 * This component is part of the unified bulk process system, sharing navigation and utility logic with other processes (box office, invite guest, import CSV).
 */

export const ProcessOfflineSales = () => {
  const formRef = useRef<HTMLFormElement>(null);
  const { handleCloseModal } = useContext(ModalContext);
  const {
    companyState: { id: companyId, defaultChannel },
    clubState: {
      id: clubId,
      city: { country },
    },
  } = useContext(CompanyClubContext);
  const { role } = useContext(CompanyAuthContext);
  const { isOfflineSalesPassScopeEnabled } = useContext(FeatureFlagContext);

  // When the pass-scope feature is gated off, skip the scope step entirely and
  // behave exactly like the pre-B2B-1593 wizard (event-only). Default scope is
  // already 'event', so no field changes — only the entry step shifts.
  const entryStep = isOfflineSalesPassScopeEnabled ? STEP_SELECT_SCOPE : STEP_SELECT_TYPE;

  // Use the extended process form hook with offline sales specific fields and proper typing
  const processForm: UseProcessFormReturn = useProcessForm({
    includeAddOns: true,
    includeSaleType: true,
    includeMultiText: true,
    includeBookingFormFields: true,
    includeTicketHolderFormFields: true,
    initialStep: entryStep,
    additionalDefaults: {
      scope: OFFLINE_SALES_SCOPE_EVENT,
      passId: '',
      saleType: SINGLE_OFFLINE_SELL,
      bookingFormFields: {},
      ticketHolderFormFields: [],
    },
  });

  const {
    methods,
    eventId,
    channelId,
    quantity,
    addOns,
    saleType: saleTypeRaw,
    bookingFormFields,
    offer,
    event,
    step: processStep,
    setStep: setProcessStep,
    closeModal: closeWizardModal,
  } = processForm;

  // Type-safe wrappers to match offline sales step types
  const step = processStep as ProcessOfflineSalesStep;
  const setStep = (newStep: ProcessOfflineSalesStep) => {
    setProcessStep(newStep);
  };

  // Type the saleType properly
  const saleType = saleTypeRaw as typeof SINGLE_OFFLINE_SELL | typeof MULTIPLE_OFFLINE_SELL;
  const scope = methods.watch('scope') as OfflineSalesScope;
  const passId = methods.watch('passId');

  // Get email from booking form fields
  const email = bookingFormFields?.email as string | undefined;

  // Availability data: events + passes (both fetched up-front so we can short-circuit
  // to the empty state when the user can't pick anything). When the pass-scope feature
  // is gated off, skip the pass fetch — only events matter. The event-list params
  // match SelectType so both share the same TanStack cache entry.
  const todayNow = useMemo(() => moment().unix(), []);
  const { data: eventList, isLoading: isLoadingEvents } = useGetEventList({
    companyId,
    clubId,
    view: 'all',
    params: {
      startTime: todayNow,
      sort: SORT_ASC,
      orderBy: 'date',
      limit: 50,
    },
  });
  // A team leader with no teams gets 404 No Channels assigned here: the wizard falls back to the
  // event path on its own, so that answer is final and must not toast nor retry.
  const { data: companyPasses, isLoading: isLoadingPasses } = useGetCompanyPasses({
    companyId,
    params: { status: ['active'], context: IMPORT_CONTEXT },
    enabled: isOfflineSalesPassScopeEnabled,
    options: { ignoredErrorCodes: [ERROR_NO_CHANNELS_ASSIGNED] },
  });
  const filteredPasses = useMemo<Pass[]>(() => {
    const merged: Pass[] = [...(companyPasses?.seasonPass ?? []), ...(companyPasses?.staffPass ?? [])];
    return merged.filter((pass) => filterByRole({ role: role?.name, isHidden: pass.settings.isHidden }));
  }, [companyPasses, role]);
  const passOptions = useMemo<SelectItem[]>(
    () =>
      filteredPasses.map((pass) => ({
        id: pass.id,
        name: `${pass.displayName} — ${formatCurrency({ currency: pass.price.currency, number: pass.price.amount })}`,
      })),
    [filteredPasses]
  );

  const hasEvents = useMemo(() => {
    const pages = eventList?.pages as EventListItem[][] | undefined;
    return !!pages?.some((page) =>
      page.some((eventItem) => filterByRole({ role: role?.name, isHidden: !!eventItem.settings?.isHidden }))
    );
  }, [eventList, role]);
  const hasPasses = filteredPasses.length > 0;
  const isCheckingAvailability = isLoadingEvents || (isOfflineSalesPassScopeEnabled && isLoadingPasses);
  const isEmptyAvailability = !isCheckingAvailability && !hasEvents && (!isOfflineSalesPassScopeEnabled || !hasPasses);
  const selectedPass = useMemo<Pass | undefined>(() => {
    if (!passId || !companyPasses) return undefined;
    return [...companyPasses.seasonPass, ...companyPasses.staffPass].find((pass) => pass.id === passId);
  }, [passId, companyPasses]);
  const { data: companyChannels, isLoading: isLoadingPassChannels } = useGetCompanyChannels({
    companyId,
    clubId,
    options: { enabled: scope === OFFLINE_SALES_SCOPE_PASS && !!companyId },
  });
  const venueIsoCode = country.isoCode.slice(0, 2);

  // "Add another" checkbox state for pass-mode AddHolderModal
  const [preserveAddHolderModal, setPreserveAddHolderModal] = useState(false);
  // Pass-mode entry is always AddBulkHolderModal (it's the pass + channel + single/multi
  // selector). User flips to 'single' explicitly via its single/multi toggle.
  const [passHolderMode, setPassHolderMode] = useState<'bulk' | 'single'>('bulk');

  const { data: addons } = useGetOfferAddon({
    offerId: offer?.id,
    enabled: offer && offer?.addOns?.length > 0,
  });
  const availableAddons = addons?.filter((addon) => {
    if (!addon.quantity) return true;

    return addon.soldItems < addon.quantity;
  });

  const {
    data: addGuestData,
    mutate: addGuest,
    isSuccess: addGuestSuccess,
    isPending: isAddingGuest,
  } = useAddGuest({ hideToast: true });
  // Pass-mode submit fires-and-forgets like list-passes — closes the modal
  // synchronously, lets the toast surface success when the API resolves.
  const { mutate: addPassHolder } = useAddGuest();
  const {
    data: importMultiInviteData,
    mutate: importMultiInvite,
    isSuccess: importSuccess,
    isPending: isImporting,
  } = useImportMultiInvite({ hideToast: true });

  // Use the shared import navigation hook
  const { closeModal } = useBulkProcessNavigation({
    eventId,
    importType: IMPORT_TYPE_OFFLINE_SALE,
    importSuccess,
    importData: importMultiInviteData,
  });

  // Narrowed event-scope payload: eventId/offerId are guaranteed by onSubmit's invariant
  // so the handlers below can treat them as required strings.
  type EventScopeForm = OfflineSalesForm & { eventId: string; offerId: string };

  const handleSingleOfflineSubmit = (e: EventScopeForm) => {
    if (typeof e.addOns === 'string') e.addOns = [e.addOns];
    addGuest({
      formValue: {
        offerId: e.offerId,
        bookingFormFields: createFieldsFromFormFields<OfflineSalesForm>(e, BOOKING_FORM_FIELDS) as FormField[],
        ticketHolderFormFields: createFieldsFromFormFields<OfflineSalesForm>(
          e,
          TICKET_HOLDER_FORM_FIELDS
        ) as FormField[][],
        numTickets: e.quantity,
        channelId: e.channelId,
        addOns: e.addOns ? e.addOns.map((addon) => ({ id: addon, quantity: e.quantity })) : [],
      },
    });
  };

  const handleMultiOfflineSaleSubmit = (e: EventScopeForm) => {
    if (typeof e.addOns === 'string') e.addOns = [e.addOns];
    if (e.multiOfferText) {
      handleBulkProcessSubmission(
        e.multiOfferText,
        {
          offerId: e.offerId,
          channelId: e.channelId,
          eventId: e.eventId,
          addOns: e.addOns,
        },
        importMultiInvite
      );
    }
  };

  const onSubmit = (e: OfflineSalesForm) => {
    if (e.scope === OFFLINE_SALES_SCOPE_PASS) return;
    // Event scope requires both ids — bail out if the form somehow reached submit without them.
    if (!e.eventId || !e.offerId) return;
    const eventForm: EventScopeForm = { ...e, eventId: e.eventId, offerId: e.offerId };
    if (saleType === SINGLE_OFFLINE_SELL) {
      handleSingleOfflineSubmit(eventForm);
    } else if (saleType === MULTIPLE_OFFLINE_SELL) {
      handleMultiOfflineSaleSubmit(eventForm);
    }
  };

  const onPassHolderSubmit = (formValue: AddGuestBody) => {
    addPassHolder({ formValue });
    if (!preserveAddHolderModal) {
      closeWizardModal();
    }
  };

  useEffect(() => {
    if (!addGuestSuccess) return;
    setStep(STEP_SUCCESS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addGuestSuccess]);

  // Auto-redirect past the scope step when only one of (events, passes) is available
  // for the current role. There is nothing to choose — render directly the matching
  // downstream step.
  const availability = useMemo(
    () => ({
      onlyEvents: isOfflineSalesPassScopeEnabled && hasEvents && !hasPasses,
      onlyPasses: isOfflineSalesPassScopeEnabled && !hasEvents && hasPasses,
    }),
    [isOfflineSalesPassScopeEnabled, hasEvents, hasPasses]
  );
  const hasResolvedEntryStepRef = useRef(false);
  useEffect(() => {
    if (hasResolvedEntryStepRef.current) return;
    if (isCheckingAvailability) return;
    if (step !== STEP_SELECT_SCOPE) {
      hasResolvedEntryStepRef.current = true;
      return;
    }
    if (availability.onlyEvents) {
      methods.setValue('scope', OFFLINE_SALES_SCOPE_EVENT);
      methods.setValue('passId', '');
      setStep(STEP_SELECT_TYPE);
      hasResolvedEntryStepRef.current = true;
      return;
    }
    if (availability.onlyPasses) {
      methods.setValue('scope', OFFLINE_SALES_SCOPE_PASS);
      methods.setValue('eventId', '');
      methods.setValue('offerId', '');
      methods.setValue('saleType', '');
      setStep(STEP_INFO);
      hasResolvedEntryStepRef.current = true;
      return;
    }
    hasResolvedEntryStepRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckingAvailability, availability.onlyEvents, availability.onlyPasses, step]);

  // Availability gating — only fires at the entry step. Once the user has continued
  // past it, downstream steps render their own data and we should not retro-actively
  // swap them out. We also keep the gate up while the auto-redirect effect is about
  // to fire, to avoid flashing the scope step for a single frame.
  const showEntryGate = useMemo(() => {
    const isAtEntryStep = step === entryStep;
    const isPendingAutoRedirect =
      !isCheckingAvailability && step === STEP_SELECT_SCOPE && (availability.onlyEvents || availability.onlyPasses);
    return (isAtEntryStep && (isCheckingAvailability || isEmptyAvailability)) || isPendingAutoRedirect;
  }, [step, entryStep, isCheckingAvailability, isEmptyAvailability, availability.onlyEvents, availability.onlyPasses]);

  return (
    <form className='flex justify-end flex-col relative' ref={formRef}>
      {showEntryGate ? (
        <OfflineSalesEntryGate isCheckingAvailability={isCheckingAvailability} onClose={handleCloseModal} />
      ) : (
        <FormProvider {...methods}>
          <OfflineSalesSteps
            step={step}
            setStep={setStep}
            offer={offer}
            event={event}
            saleType={saleType}
            quantity={quantity}
            addGuestData={addGuestData}
            availableAddons={availableAddons ?? []}
            selectedAddonIds={typeof addOns === 'string' ? [addOns] : addOns}
            email={email}
            isSubmitting={isAddingGuest || isImporting}
            closeModal={closeModal}
            handleCloseModal={handleCloseModal}
            onSubmit={onSubmit}
            scope={scope}
            selectedPass={selectedPass}
            passModalChannels={companyChannels ?? []}
            isLoadingPassChannels={isLoadingPassChannels}
            defaultPassChannelId={channelId || defaultChannel?.id}
            venueIsoCode={venueIsoCode}
            onPassHolderSubmit={onPassHolderSubmit}
            preserveAddHolderModal={preserveAddHolderModal}
            onPreserveAddHolderModalChange={setPreserveAddHolderModal}
            closeWizardModal={closeWizardModal}
            passHolderMode={passHolderMode}
            onSelectSinglePassHolder={(newPassId: string | undefined) => {
              if (newPassId) methods.setValue('passId', newPassId, { shouldValidate: true });
              setPassHolderMode('single');
            }}
            passOptions={passOptions}
            entryStep={entryStep}
          />
        </FormProvider>
      )}
    </form>
  );
};
